import { Language, UserLang } from '@constants/common';
import { useTranslation } from 'gatsby-plugin-react-i18next';

function useRenderLanguage() {
    const { t: translate } = useTranslation();

    const getTranslationObjectByUserLang = (userLang: UserLang) => {
        switch (userLang) {
            case UserLang.English:
                return {
                    text: 'English',
                    shortText: UserLang.English.toUpperCase(),
                    flag: '🇬🇧',
                };
            case UserLang.French:
                return {
                    text: 'Français',
                    flag: '🇫🇷',
                    shortText: UserLang.French.toUpperCase(),
                };
            case UserLang.Italian:
                return {
                    text: 'Italiano',
                    flag: '🇮🇹',
                    shortText: UserLang.Italian.toUpperCase(),
                };
            case UserLang.German:
            default:
                return {
                    text: 'Deutsch',
                    flag: '🇩🇪',
                    shortText: UserLang.German.toUpperCase(),
                };
        }
    };

    function getTranslationObject(lang: Language, noFlag = false) {
        switch (lang) {
            case Language.SwissGerman:
                return {
                    flag: noFlag ? '' : '🇨🇭',
                    text: translate('language.swiss.german'),
                };
            case Language.German:
                return {
                    flag: noFlag ? '' : '🇩🇪',
                    text: translate('language.german'),
                };
            case Language.French:
                return {
                    flag: noFlag ? '' : '🇫🇷',
                    text: translate('language.french'),
                };
            case Language.English:
                return {
                    flag: noFlag ? '' : '🇬🇧',
                    text: translate('language.english'),
                };
            case Language.Italian:
                return {
                    flag: noFlag ? '' : '🇮🇹',
                    text: translate('language.italian'),
                };
            case Language.Bosnian:
                return {
                    flag: noFlag ? '' : '🇧🇦',
                    text: translate('language.bosnian'),
                };
            case Language.Croatian:
                return {
                    flag: noFlag ? '' : '🇭🇷',
                    text: translate('language.croatian'),
                };
            case Language.Serbian:
                return {
                    flag: noFlag ? '' : '🇷🇸',
                    text: translate('language.serbian'),
                };
            case Language.Luxembourghish:
                return {
                    flag: noFlag ? '' : '🇱🇺',
                    text: translate('language.luxembourg'),
                };
            case Language.Romanisch:
                return {
                    flag: noFlag ? '' : '🇨🇭',
                    text: translate('language.romanisch'),
                };
            case Language.Turkish:
                return {
                    flag: noFlag ? '' : '🇹🇷',
                    text: translate('language.turkish'),
                };
            case Language.Hungarian:
                return {
                    flag: noFlag ? '' : '🇭🇺',
                    text: translate('language.hungarian'),
                };
            case Language.Sweden:
                return {
                    flag: noFlag ? '' : '🇸🇪',
                    text: translate('language.swedish'),
                };
            case Language.Spanish:
                return {
                    flag: noFlag ? '' : '🇪🇸',
                    text: translate('language.spanish'),
                };
            case Language.Dutch:
                return {
                    flag: noFlag ? '' : '🇳🇱',
                    text: translate('language.dutch'),
                };
            case Language.Portuguese:
                return {
                    flag: noFlag ? '' : '🇵🇹',
                    text: translate('language.portuguese'),
                };
            default:
                return {
                    flag: '',
                    text: '',
                };
        }
    }

    function renderLanguage(lang: Language, noFlag = false): string {
        const { flag, text } = getTranslationObject(lang, noFlag);

        return `${flag} ${text}`;
    }

    function renderLanguageFlag(lang: Language): string {
        const { flag } = getTranslationObject(lang);

        return flag;
    }

    function renderLanguageByUserLang(
        userLang: UserLang,
        isShort = true
    ): string {
        const { text, shortText } = getTranslationObjectByUserLang(userLang);

        return isShort ? shortText : text;
    }

    return {
        renderLanguage,
        renderLanguageFlag,
        renderLanguageByUserLang,
        getTranslationObjectByUserLang,
    };
}

export default useRenderLanguage;
